import React, { useEffect, useRef, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { printHtml } from "../lib/print";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import { teluguImage } from "../lib/teluguImage";
import CounterShiftFilter, { scopeLabel, useCounterShiftMeta } from "../components/CounterShiftFilter";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Printer, Landmark, Flame, FileDown } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";

const MAROON = [128, 0, 32];

let brandingLogo = "";
const brandingReady = api.get("/public/branding").then((r) => { brandingLogo = r.data.logo || ""; }).catch(() => {});
function useBrandingLogo() {
  const [logo, setLogo] = useState(brandingLogo);
  useEffect(() => { brandingReady.then(() => setLogo(brandingLogo)); }, []);
  return logo;
}
const imgFormat = (dataUrl) => (/^data:image\/jpe?g/i.test(dataUrl) ? "JPEG" : "PNG");
const todayFile = () => new Date().toISOString().slice(0, 10);

export async function makePdf({ temple, templeName, title, head, body, foot, filename, landscape = false, fontSize = 8 }) {
  const tel = temple?.name_telugu && (!templeName || templeName === temple?.name) ? await teluguImage(temple.name_telugu) : null;
  const doc = new jsPDF({ orientation: landscape ? "landscape" : "portrait", unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const cx = W / 2;
  const logo = temple?.logo || brandingLogo;
  if (logo) {
    try { doc.addImage(logo, imgFormat(logo), 14, 8, 22, 22); } catch { /* logo skipped */ }
  }
  if (temple?.logo_right) {
    try { doc.addImage(temple.logo_right, imgFormat(temple.logo_right), W - 36, 8, 22, 22); } catch { /* logo skipped */ }
  }
  if (tel) {
    const h = 7, w = Math.min(h * tel.ratio, W - 90);
    doc.addImage(tel.data, "PNG", cx - w / 2, 3.2, w, w / tel.ratio);
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(templeName || temple?.name || "All Temples", cx, tel ? 15.5 : 15, { align: "center" });
  if (temple?.report_header) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(temple.report_header, cx, 20, { align: "center" });
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(title, cx, 25, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(`Generated: ${new Date().toLocaleString("en-IN")}`, cx, 30, { align: "center" });
  autoTable(doc, {
    startY: 35, head: [head], body, foot: foot ? [foot] : undefined,
    styles: { fontSize, cellPadding: 1.5 },
    headStyles: { fillColor: MAROON, textColor: 255 },
    footStyles: { fillColor: [253, 246, 227], textColor: MAROON, fontStyle: "bold" },
  });
  // signature block pinned to bottom of the last A4 page
  doc.setPage(doc.getNumberOfPages());
  const labels = [temple?.sign_label_1 || "Counter Signature", temple?.sign_label_2 || "Superintendent", temple?.sign_label_3 || "Executive Officer"];
  const xs = [W / 6, cx, (W * 5) / 6];
  const y = H - 25;
  doc.setFontSize(9);
  labels.forEach((label, i) => {
    doc.line(xs[i] - 28, y, xs[i] + 28, y);
    doc.setFont("helvetica", "bold");
    doc.text(label, xs[i], y + 5, { align: "center" });
  });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(temple?.report_footer || "Aatreya Infotech Systems - Temple ERP", cx, H - 7, { align: "center" });
  doc.save(filename);
}

// Temple-wise logo: the report's temple logo, else the app (Aatreya) logo.
export function ReportHeader({ title, temple }) {
  const { user } = useAuth();
  const brand = useBrandingLogo();
  const logo = temple?.logo || brand;
  return (
    <div className="flex items-center gap-3 border-b-2 border-maroon-800 pb-3 mb-4">
      {logo ? <img src={logo} alt="logo" className="w-14 h-14 object-contain" data-testid="report-logo" />
        : <Landmark className="w-9 h-9 text-maroon-800" />}
      <div className="flex-1 text-center">
        {temple?.name_telugu && <p className="font-telugu text-2xl text-maroon-800 leading-tight" data-testid="report-temple-telugu">{temple.name_telugu}</p>}
        <h2 className="font-heading text-xl font-bold text-maroon-900">{temple?.name || user.temple_name || "All Temples"}</h2>
        <p className="text-xs uppercase tracking-widest text-gold-700">{title}</p>
        <p className="text-xs text-slate-500">Generated: {new Date().toLocaleString("en-IN")}</p>
      </div>
      {temple?.logo_right ? <img src={temple.logo_right} alt="logo" className="w-14 h-14 object-contain" data-testid="report-logo-right" />
        : logo ? <div className="w-14 h-14" /> : <Flame className="w-9 h-9 text-maroon-800" />}
    </div>
  );
}

function DCR() {
  const { user } = useAuth();
  const [meta, setMeta] = useState({ darshan_types: [], counters: [], shifts: [], temples: [] });
  const [f, setF] = useState({ frm: "", to: "", shift_id: "", counter_id: "", darshan_type_id: "", booking_type: "", paid_free: "", temple_id: "" });
  const [data, setData] = useState(null);
  const reportRef = useRef(null);

  useEffect(() => { api.get("/meta/form-data").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const run = () => {
    const q = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && q.set(k, v));
    api.get(`/reports/dcr?${q}`).then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e)));
  };
  useEffect(() => { run(); }, []); // eslint-disable-line

  const downloadPdf = () => {
    if (!data || data.rows.length === 0) { toast.error("No data to export"); return; }
    const temple = meta.temples.find((t) => t.temple_id === (f.temple_id || user.temple_id));
    makePdf({
      temple,
      title: "Daily Collection Report (DCR) - Protocol Darshan" + scopeLabel(user, meta, f).replace(/·/g, "-"),
      head: ["S.No", "Darshan Type", "Start Token", "End Token", "Tickets", "Devotees", "Price", "Amount"],
      body: data.rows.map((r, i) => [i + 1, r.darshan_type, r.start_token, r.end_token, r.tickets, r.devotees, `Rs ${r.price}`, `Rs ${r.amount.toLocaleString("en-IN")}`]),
      foot: ["", "", "", "Grand Total", data.totals.tickets, data.totals.devotees, "", `Rs ${data.totals.amount.toLocaleString("en-IN")}`],
      filename: `DCR_${todayFile()}.pdf`,
    });
  };

  const sel = (key, label, items, valueKey, labelKey) => (
    <div><Label className="text-xs">{label}</Label>
      <Select value={f[key]} onValueChange={(v) => setF({ ...f, [key]: v === "ALL" ? "" : v })}>
        <SelectTrigger data-testid={`dcr-filter-${key}`}><SelectValue placeholder="All" /></SelectTrigger>
        <SelectContent><SelectItem value="ALL">All</SelectItem>{items.map((i) => <SelectItem key={i[valueKey]} value={i[valueKey]}>{i[labelKey]}</SelectItem>)}</SelectContent>
      </Select></div>
  );

  return (
    <div>
      <Card className="shadow-sm mb-4">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div><Label className="text-xs">From Date</Label><Input type="date" value={f.frm} onChange={(e) => setF({ ...f, frm: e.target.value })} data-testid="dcr-from" /></div>
          <div><Label className="text-xs">To Date</Label><Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="dcr-to" /></div>
          {user.role === "SUPER_ADMIN" && sel("temple_id", "Temple", meta.temples, "temple_id", "name")}
          <CounterShiftFilter user={user} meta={meta} value={f} onChange={setF} testid="dcr-filter" />
          {sel("darshan_type_id", "Darshan Type", meta.darshan_types, "id", "name")}
          <div><Label className="text-xs">Booking Type</Label>
            <Select value={f.booking_type} onValueChange={(v) => setF({ ...f, booking_type: v === "ALL" ? "" : v })}>
              <SelectTrigger data-testid="dcr-filter-booking"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">All</SelectItem><SelectItem value="ADVANCED">Advanced</SelectItem><SelectItem value="CURRENT">Current</SelectItem></SelectContent>
            </Select></div>
          <div className="flex gap-2">
            <Select value={f.paid_free} onValueChange={(v) => setF({ ...f, paid_free: v === "ALL" ? "" : v })}>
              <SelectTrigger data-testid="dcr-filter-paid"><SelectValue placeholder="Paid/Free" /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">All</SelectItem><SelectItem value="PAID">Paid</SelectItem><SelectItem value="FREE">Free</SelectItem></SelectContent>
            </Select>
            <Button onClick={run} data-testid="dcr-run-btn" className="bg-maroon-700 hover:bg-maroon-800 text-white">View</Button>
            <Button onClick={downloadPdf} variant="outline" title="Download PDF (A4)" data-testid="dcr-pdf-btn"><FileDown className="w-4 h-4" /></Button>
            <Button onClick={() => reportRef.current && printHtml(reportRef.current.outerHTML, "a4-page", `DCR_Report_${new Date().toLocaleDateString("en-CA")}`)} variant="outline" title="Print (A4 portrait)" data-testid="dcr-print-btn"><Printer className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <div ref={reportRef} className="print-area print-a4 bg-white border border-[#E5D9C5] rounded-md shadow-sm p-6" data-testid="dcr-report">
          <ReportHeader title={"Daily Collection Report (DCR) — Protocol Darshan" + scopeLabel(user, meta, f)} temple={meta.temples.find((t) => t.temple_id === (f.temple_id || user.temple_id))} />
          <table className="w-full text-sm border border-slate-300">
            <thead>
              <tr className="bg-maroon-800 text-white text-xs uppercase tracking-wider">
                <th className="py-2 px-3 text-left">S.No</th><th className="py-2 px-3 text-left">Darshan Type</th>
                <th className="py-2 px-3 text-left">Start Token</th><th className="py-2 px-3 text-left">End Token</th>
                <th className="py-2 px-3 text-right">Tickets</th><th className="py-2 px-3 text-right">Devotees</th>
                <th className="py-2 px-3 text-right">Price</th><th className="py-2 px-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-slate-400">No issued tickets in the selected period</td></tr>}
              {data.rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-200" data-testid={`dcr-row-${i}`}>
                  <td className="py-2 px-3">{i + 1}</td>
                  <td className="py-2 px-3 font-semibold">{r.darshan_type}</td>
                  <td className="py-2 px-3 font-mono text-xs">{r.start_token}</td>
                  <td className="py-2 px-3 font-mono text-xs">{r.end_token}</td>
                  <td className="py-2 px-3 text-right">{r.tickets}</td>
                  <td className="py-2 px-3 text-right">{r.devotees}</td>
                  <td className="py-2 px-3 text-right">₹{r.price}</td>
                  <td className="py-2 px-3 text-right">₹{r.amount.toLocaleString("en-IN")}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-maroon-800 bg-gold-50 font-bold" data-testid="dcr-totals">
                <td className="py-2 px-3" colSpan={4}>Grand Total (Paid: {data.totals.paid} · Free: {data.totals.free})</td>
                <td className="py-2 px-3 text-right">{data.totals.tickets}</td>
                <td className="py-2 px-3 text-right">{data.totals.devotees}</td>
                <td className="py-2 px-3" />
                <td className="py-2 px-3 text-right">₹{data.totals.amount.toLocaleString("en-IN")}</td>
              </tr>
            </tbody>
          </table>
          <div className="flex justify-between mt-10 text-xs text-slate-600">
            <div className="text-center"><p className="border-t border-slate-400 pt-1 px-6">Counter Signature</p></div>
            <div className="text-center"><p className="border-t border-slate-400 pt-1 px-6">Superintendent</p></div>
            <div className="text-center"><p className="border-t border-slate-400 pt-1 px-6">Executive Officer</p></div>
          </div>
        </div>
      )}
    </div>
  );
}

// Temple users: this temple's abstract by darshan type. Super Admin: all temples, or pick one temple for its darshan-wise view.
function TempleAbstract() {
  const { user } = useAuth();
  const isSA = user.role === "SUPER_ADMIN";
  const [rows, setRows] = useState([]);
  const [temples, setTemples] = useState([]);
  const csMeta = useCounterShiftMeta();
  const [f, setF] = useState({ frm: "", to: "", shift_id: "", counter_id: "", temple_id: "" });
  const [shown, setShown] = useState({ temple_id: "" });
  const run = () => {
    const q = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && q.set(k, v));
    api.get(`/reports/temple-abstract?${q}`).then((r) => { setRows(r.data); setShown({ temple_id: f.temple_id }); }).catch((e) => toast.error(fmtErr(e)));
  };
  useEffect(() => {
    run();
    api.get("/temples").then((r) => setTemples(r.data)).catch(() => {});
  }, []); // eslint-disable-line
  const byDarshan = !isSA || !!shown.temple_id;
  const temple = temples.find((t) => t.temple_id === (shown.temple_id || user.temple_id));
  const title = (byDarshan ? "Darshan-wise Abstract" : "Temple-wise Abstract") + scopeLabel(user, csMeta, f);
  const totals = rows.reduce((a, r) => ({ transactions: a.transactions + r.transactions, devotees: a.devotees + r.devotees, paid: a.paid + r.paid, free: a.free + r.free, amount: a.amount + r.amount }),
    { transactions: 0, devotees: 0, paid: 0, free: 0, amount: 0 });
  const first = byDarshan ? "Darshan Type" : "Temple";
  const downloadPdf = () => {
    if (rows.length === 0) { toast.error("No data to export"); return; }
    makePdf({
      temple, templeName: byDarshan ? temple?.name : "All Temples", title: title.replace(/·/g, "-"),
      head: [first, "Transactions", "Devotees", "Paid", "Free", "Amount"],
      body: rows.map((r) => [r.label, r.transactions, r.devotees, r.paid, r.free, `Rs ${r.amount.toLocaleString("en-IN")}`]),
      foot: ["Total", totals.transactions, totals.devotees, totals.paid, totals.free, `Rs ${totals.amount.toLocaleString("en-IN")}`],
      filename: `${byDarshan ? "DarshanAbstract" : "TempleAbstract"}_${todayFile()}.pdf`,
    });
  };
  return (
    <>
    <Card className="shadow-sm mb-4">
      <CardContent className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
        <div><Label className="text-xs">From Date</Label><Input type="date" value={f.frm} onChange={(e) => setF({ ...f, frm: e.target.value })} data-testid="temple-abstract-from" /></div>
        <div><Label className="text-xs">To Date</Label><Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="temple-abstract-to" /></div>
        {isSA && (
          <div><Label className="text-xs">Temple</Label>
            <Select value={f.temple_id || "ALL"} onValueChange={(v) => setF({ ...f, temple_id: v === "ALL" ? "" : v })}>
              <SelectTrigger data-testid="temple-abstract-temple"><SelectValue placeholder="All temples" /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">All temples (temple-wise)</SelectItem>
                {temples.map((t) => <SelectItem key={t.temple_id} value={t.temple_id}>{t.name}</SelectItem>)}</SelectContent>
            </Select></div>
        )}
        <CounterShiftFilter user={user} meta={csMeta} value={f} onChange={setF} testid="temple-abstract" />
        <Button onClick={run} data-testid="temple-abstract-run-btn" className="bg-maroon-700 hover:bg-maroon-800 text-white">View</Button>
      </CardContent>
    </Card>
    <Card className="shadow-sm" data-testid="temple-abstract">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-heading text-lg text-maroon-900">{title}</CardTitle>
          {byDarshan && temple && <p className="text-sm text-slate-500 mt-1">{temple.name}</p>}
        </div>
        <Button size="sm" variant="outline" onClick={downloadPdf} data-testid="temple-abstract-pdf-btn"><FileDown className="w-4 h-4 mr-1" /> PDF</Button>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-gold-700 border-b border-[#E5D9C5]">
            <th className="py-2 pr-4">{first}</th><th className="py-2 pr-4 text-right">Transactions</th><th className="py-2 pr-4 text-right">Devotees</th>
            <th className="py-2 pr-4 text-right">Paid</th><th className="py-2 pr-4 text-right">Free</th><th className="py-2 text-right">Amount</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">No data</td></tr>}
            {rows.map((r) => (
              <tr key={r.label} className="border-b border-[#F1EADB]" data-testid={`abstract-row-${r.label}`}>
                <td className="py-2 pr-4 font-semibold">{r.label}</td>
                <td className="py-2 pr-4 text-right">{r.transactions}</td>
                <td className="py-2 pr-4 text-right">{r.devotees}</td>
                <td className="py-2 pr-4 text-right">{r.paid}</td>
                <td className="py-2 pr-4 text-right">{r.free}</td>
                <td className="py-2 text-right">₹{r.amount.toLocaleString("en-IN")}</td>
              </tr>
            ))}
            {rows.length > 0 && (
              <tr className="border-t-2 border-maroon-800 bg-gold-50 font-bold" data-testid="abstract-totals">
                <td className="py-2 pr-4">Total</td>
                <td className="py-2 pr-4 text-right">{totals.transactions}</td>
                <td className="py-2 pr-4 text-right">{totals.devotees}</td>
                <td className="py-2 pr-4 text-right">{totals.paid}</td>
                <td className="py-2 pr-4 text-right">{totals.free}</td>
                <td className="py-2 text-right">₹{totals.amount.toLocaleString("en-IN")}</td>
              </tr>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
    </>
  );
}

function DailyReport() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [temple, setTemple] = useState(null);
  const csMeta = useCounterShiftMeta();
  const [f, setF] = useState({ frm: "", to: "", shift_id: "", counter_id: "" });
  const run = () => {
    const q = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && q.set(k, v));
    api.get(`/reports/daily?${q}`).then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e)));
  };
  useEffect(() => {
    run();
    api.get("/temples").then((r) => setTemple(r.data.find((t) => t.temple_id === user.temple_id))).catch(() => {});
  }, []); // eslint-disable-line
  const downloadPdf = () => {
    if (rows.length === 0) { toast.error("No data to export"); return; }
    makePdf({
      temple, templeName: temple?.name || "All Temples", title: "Day-wise Abstract" + scopeLabel(user, csMeta, f).replace(/·/g, "-"),
      head: ["Date", "Transactions", "Devotees", "Paid", "Free", "Revenue"],
      body: rows.map((r) => [r.date, r.transactions, r.devotees, r.paid, r.free, `Rs ${r.amount.toLocaleString("en-IN")}`]),
      filename: `DailyAbstract_${todayFile()}.pdf`,
    });
  };
  return (
    <>
    <Card className="shadow-sm mb-4">
      <CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
        <div><Label className="text-xs">From Date</Label><Input type="date" value={f.frm} onChange={(e) => setF({ ...f, frm: e.target.value })} data-testid="daily-from" /></div>
        <div><Label className="text-xs">To Date</Label><Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="daily-to" /></div>
        <CounterShiftFilter user={user} meta={csMeta} value={f} onChange={setF} testid="daily" />
        <Button onClick={run} data-testid="daily-run-btn" className="bg-maroon-700 hover:bg-maroon-800 text-white">View</Button>
      </CardContent>
    </Card>
    <Card className="shadow-sm" data-testid="daily-report">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-lg text-maroon-900">Day-wise Abstract{scopeLabel(user, csMeta, f)}</CardTitle>
        <Button size="sm" variant="outline" onClick={downloadPdf} data-testid="daily-pdf-btn"><FileDown className="w-4 h-4 mr-1" /> PDF</Button>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-gold-700 border-b border-[#E5D9C5]">
            <th className="py-2 pr-4">Date</th><th className="py-2 pr-4 text-right">Transactions</th><th className="py-2 pr-4 text-right">Devotees</th>
            <th className="py-2 pr-4 text-right">Paid</th><th className="py-2 pr-4 text-right">Free</th><th className="py-2 text-right">Revenue</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">No data</td></tr>}
            {rows.map((r) => (
              <tr key={r.date} className="border-b border-[#F1EADB]" data-testid={`daily-row-${r.date}`}>
                <td className="py-2 pr-4 font-semibold">{r.date}</td>
                <td className="py-2 pr-4 text-right">{r.transactions}</td>
                <td className="py-2 pr-4 text-right">{r.devotees}</td>
                <td className="py-2 pr-4 text-right">{r.paid}</td>
                <td className="py-2 pr-4 text-right">{r.free}</td>
                <td className="py-2 text-right">₹{r.amount.toLocaleString("en-IN")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
    </>
  );
}

function WhatsAppLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/whatsapp").then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e))); }, []);
  return (
    <Card className="shadow-sm" data-testid="whatsapp-log">
      <CardHeader><CardTitle className="font-heading text-lg text-maroon-900">WhatsApp Message Log</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {rows.length === 0 && <p className="text-sm text-slate-400 py-4 text-center">No messages yet</p>}
        {rows.map((m) => (
          <div key={m.id} className="border border-[#E5D9C5] rounded-md p-3 bg-gold-50/40" data-testid={`wa-msg-${m.id}`}>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mb-1">
              <span className="font-bold text-maroon-800">{m.template.replace(/_/g, " ")}</span>
              <span>To: {m.mobile}</span>
              <span className="font-mono">{m.transaction_id}</span>
              <span>{new Date(m.sent_at).toLocaleString("en-IN")}</span>
              <span className="font-bold text-green-700">{m.status}</span>
            </div>
            <pre className="whitespace-pre-wrap text-sm text-slate-700 font-body">{m.body}</pre>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function AuditLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/reports/audit").then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e))); }, []);
  return (
    <Card className="shadow-sm" data-testid="audit-log">
      <CardHeader><CardTitle className="font-heading text-lg text-maroon-900">Audit Log</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-gold-700 border-b border-[#E5D9C5]">
            <th className="py-2 pr-4">Time</th><th className="py-2 pr-4">User</th><th className="py-2 pr-4">Action</th>
            <th className="py-2 pr-4">Module</th><th className="py-2 pr-4">Record</th><th className="py-2 pr-4">Details</th><th className="py-2">IP</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-400">No audit records</td></tr>}
            {rows.map((a) => (
              <tr key={a.id} className="border-b border-[#F1EADB]" data-testid={`audit-row-${a.id}`}>
                <td className="py-2 pr-4 text-xs text-slate-500 whitespace-nowrap">{new Date(a.created_at).toLocaleString("en-IN")}</td>
                <td className="py-2 pr-4">{a.username}</td>
                <td className="py-2 pr-4 font-semibold text-maroon-800">{a.action}</td>
                <td className="py-2 pr-4">{a.module}</td>
                <td className="py-2 pr-4 font-mono text-xs">{a.record_id}</td>
                <td className="py-2 pr-4 text-xs">{[a.old_value, a.new_value].filter(Boolean).join(" → ")} {a.reason ? `· ${a.reason}` : ""}</td>
                <td className="py-2 text-xs">{a.ip_address}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function ApprovalReport() {
  const { user } = useAuth();
  const [f, setF] = useState({ frm: "", to: "", status: "" });
  const [rows, setRows] = useState([]);
  const [temple, setTemple] = useState(null);
  const load = () => {
    const q = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && q.set(k, v));
    api.get(`/reports/approvals?${q}`).then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e)));
  };
  useEffect(() => {
    load();
    api.get("/temples").then((r) => setTemple(r.data.find((t) => t.temple_id === user.temple_id))).catch(() => {});
  }, []); // eslint-disable-line
  const downloadPdf = () => {
    if (rows.length === 0) { toast.error("No data to export"); return; }
    makePdf({
      temple, templeName: temple?.name || "All Temples", title: "Protocol Approval Report",
      head: ["Transaction ID", "Devotee", "Darshan", "Devotees", "Status", "Submitted", "Action By", "Remarks"],
      body: rows.map((t) => [
        t.transaction_id, t.devotee_name, t.darshan_type_name, t.num_devotees, t.status.replace(/_/g, " "),
        new Date(t.created_at).toLocaleDateString("en-IN"),
        t.approved_by || t.rejected_by || "-",
        t.approval_remarks || t.rejection_reason || "-",
      ]),
      filename: `ApprovalReport_${todayFile()}.pdf`,
    });
  };
  return (
    <div>
      <Card className="shadow-sm mb-4"><CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
        <div><Label className="text-xs">From Date</Label><Input type="date" value={f.frm} onChange={(e) => setF({ ...f, frm: e.target.value })} data-testid="approval-report-from" /></div>
        <div><Label className="text-xs">To Date</Label><Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="approval-report-to" /></div>
        <div><Label className="text-xs">Status</Label>
          <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v === "ALL" ? "" : v })}>
            <SelectTrigger data-testid="approval-report-status"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent><SelectItem value="ALL">All</SelectItem>
              {["PENDING_APPROVAL", "APPROVED", "PHOTO_CAPTURED", "ISSUED", "DARSHAN_COMPLETED", "REJECTED", "CANCELLED"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select></div>
        <div className="flex gap-2">
          <Button onClick={load} data-testid="approval-report-run" className="bg-maroon-700 hover:bg-maroon-800 text-white">View</Button>
          <Button onClick={downloadPdf} variant="outline" data-testid="approval-report-pdf"><FileDown className="w-4 h-4" /></Button>
        </div>
      </CardContent></Card>
      <Card className="shadow-sm"><CardContent className="overflow-x-auto p-4">
        <table className="w-full text-sm" data-testid="approval-report-table">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-gold-700 border-b border-[#E5D9C5]">
            <th className="py-2 pr-4">Transaction ID</th><th className="py-2 pr-4">Devotee</th><th className="py-2 pr-4">Darshan</th>
            <th className="py-2 pr-4">Devotees</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Submitted</th>
            <th className="py-2 pr-4">Action By</th><th className="py-2 pr-4">Action Date</th><th className="py-2">Remarks</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400">No records</td></tr>}
            {rows.map((t) => (
              <tr key={t.transaction_id} className="border-b border-[#F1EADB]" data-testid={`approval-row-${t.transaction_id}`}>
                <td className="py-2 pr-4 font-mono text-xs">{t.transaction_id}</td>
                <td className="py-2 pr-4">{t.devotee_name}</td>
                <td className="py-2 pr-4">{t.darshan_type_name}</td>
                <td className="py-2 pr-4">{t.num_devotees}</td>
                <td className="py-2 pr-4"><StatusBadge status={t.status} /></td>
                <td className="py-2 pr-4 text-xs">{new Date(t.created_at).toLocaleDateString("en-IN")}</td>
                <td className="py-2 pr-4">{t.approved_by || t.rejected_by || "—"}</td>
                <td className="py-2 pr-4 text-xs">{(t.approved_at || t.rejected_at) ? new Date(t.approved_at || t.rejected_at).toLocaleString("en-IN") : "—"}</td>
                <td className="py-2 text-xs">{t.approval_remarks || t.rejection_reason || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}

const TXN_STATUSES = ["PENDING_APPROVAL", "APPROVED", "PHOTO_CAPTURED", "ISSUED", "DARSHAN_COMPLETED", "REJECTED", "CANCELLED"];
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");
// Counter/shift that handled the ticket, else where it was booked.
const dmyDate = (d) => (d ? d.split("-").reverse().join("/") : "-");
const handledBy = (r, key) => r.ticket?.[key] || r[key] || "-";
const DETAIL_HEAD = ["S.No", "Transaction ID", "Date & Time", "Darshan Date", "Ticket No", "Devotee", "Reference Name", "Letterhead Name", "Mobile", "Darshan", "Type", "Devotees (P/F)", "Amount", "Counter", "Shift", "Status", "Verified At", "Verified By"];

function DetailsReport() {
  const { user } = useAuth();
  const [meta, setMeta] = useState({ darshan_types: [], counters: [], shifts: [], temples: [] });
  const [f, setF] = useState({ frm: "", to: "", shift_id: "", counter_id: "", darshan_type_id: "", booking_type: "", paid_free: "", status: "", temple_id: "" });
  const [data, setData] = useState(null);
  const reportRef = useRef(null);

  useEffect(() => { api.get("/meta/form-data").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const run = () => {
    const q = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && q.set(k, v));
    api.get(`/reports/details?${q}`).then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e)));
  };
  useEffect(() => { run(); }, []); // eslint-disable-line

  const title = "Protocol Darshan - Details Report" + scopeLabel(user, meta, f);
  const downloadPdf = () => {
    if (!data || data.rows.length === 0) { toast.error("No data to export"); return; }
    const temple = meta.temples.find((t) => t.temple_id === (f.temple_id || user.temple_id));
    makePdf({
      temple, landscape: true, fontSize: 6.5,
      title: title.replace(/·/g, "-"),
      head: DETAIL_HEAD,
      body: data.rows.map((r, i) => [i + 1, r.transaction_id, fmtDateTime(r.created_at), dmyDate(r.darshan_date), r.ticket?.ticket_no || "-", r.devotee_name, r.reference_name || "-", r.letterhead_name || "-", r.mobile,
        r.darshan_type_name, r.booking_type === "ADVANCED" ? "Advanced" : "Current", `${r.num_devotees} (${r.paid_devotees ?? "-"}/${r.free_devotees ?? "-"})`,
        `Rs ${(r.amount || 0).toLocaleString("en-IN")}`, handledBy(r, "counter_name"), handledBy(r, "shift_name"), r.status.replace(/_/g, " "),
        r.verified_at ? fmtDateTime(r.verified_at) : "-", r.verified_by || "-"]),
      foot: ["", "", "", "", "", "", "", "", "", "", "Total", `${data.totals.devotees} (${data.totals.paid_devotees}/${data.totals.free_devotees})`,
        `Rs ${data.totals.amount.toLocaleString("en-IN")}`, "", "", `${data.totals.count} records`, "", ""],
      filename: `ProtocolDetails_${todayFile()}.pdf`,
    });
  };

  const sel = (key, label, items, valueKey, labelKey) => (
    <div><Label className="text-xs">{label}</Label>
      <Select value={f[key]} onValueChange={(v) => setF({ ...f, [key]: v === "ALL" ? "" : v })}>
        <SelectTrigger data-testid={`details-filter-${key}`}><SelectValue placeholder="All" /></SelectTrigger>
        <SelectContent><SelectItem value="ALL">All</SelectItem>{items.map((i) => <SelectItem key={i[valueKey]} value={i[valueKey]}>{i[labelKey]}</SelectItem>)}</SelectContent>
      </Select></div>
  );

  return (
    <div>
      <Card className="shadow-sm mb-4">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div><Label className="text-xs">From Date</Label><Input type="date" value={f.frm} onChange={(e) => setF({ ...f, frm: e.target.value })} data-testid="details-from" /></div>
          <div><Label className="text-xs">To Date</Label><Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="details-to" /></div>
          {user.role === "SUPER_ADMIN" && sel("temple_id", "Temple", meta.temples, "temple_id", "name")}
          <CounterShiftFilter user={user} meta={meta} value={f} onChange={setF} testid="details-filter" />
          {sel("darshan_type_id", "Darshan Type", meta.darshan_types, "id", "name")}
          {sel("booking_type", "Booking Type", [{ v: "ADVANCED", l: "Advanced" }, { v: "CURRENT", l: "Current" }], "v", "l")}
          {sel("paid_free", "Paid / Free", [{ v: "PAID", l: "Paid" }, { v: "FREE", l: "Free" }, { v: "MIXED", l: "Mixed" }], "v", "l")}
          {sel("status", "Status", TXN_STATUSES.map((x) => ({ v: x, l: x.replace(/_/g, " ") })), "v", "l")}
          <div className="flex gap-2">
            <Button onClick={run} data-testid="details-run-btn" className="bg-maroon-700 hover:bg-maroon-800 text-white">View</Button>
            <Button onClick={downloadPdf} variant="outline" title="Download PDF (A4 landscape)" data-testid="details-pdf-btn"><FileDown className="w-4 h-4" /></Button>
            <Button onClick={() => reportRef.current && printHtml(reportRef.current.outerHTML, "a4-page", `Protocol_Details_Report_${new Date().toLocaleDateString("en-CA")}`)} variant="outline" title="Print" data-testid="details-print-btn"><Printer className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <div ref={reportRef} className="print-area print-a4 bg-white border border-[#E5D9C5] rounded-md shadow-sm p-6 overflow-x-auto" data-testid="details-report">
          <ReportHeader title={title} temple={meta.temples.find((t) => t.temple_id === (f.temple_id || user.temple_id))} />
          <table className="w-full text-xs border border-slate-300">
            <thead>
              <tr className="bg-maroon-800 text-white uppercase tracking-wider">
                {DETAIL_HEAD.map((h) => (
                  <th key={h} className={`py-2 px-2 whitespace-nowrap ${h === "Amount" || h.startsWith("Devotees") ? "text-right" : "text-left"}`}>{h}</th>))}
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={18} className="py-6 text-center text-slate-400">No transactions for the selected filters</td></tr>}
              {data.rows.map((r, i) => (
                <tr key={r.transaction_id} className={`border-t border-slate-200 ${["CANCELLED", "REJECTED"].includes(r.status) ? "text-slate-400 line-through" : ""}`} data-testid={`details-row-${i}`}>
                  <td className="py-1.5 px-2">{i + 1}</td>
                  <td className="py-1.5 px-2 font-mono">{r.transaction_id}</td>
                  <td className="py-1.5 px-2 whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                  <td className="py-1.5 px-2 whitespace-nowrap font-semibold text-maroon-800">{dmyDate(r.darshan_date)}</td>
                  <td className="py-1.5 px-2 font-mono">{r.ticket?.ticket_no || "-"}</td>
                  <td className="py-1.5 px-2 font-semibold">{r.devotee_name}</td>
                  <td className="py-1.5 px-2">{r.reference_name || "-"}</td>
                  <td className="py-1.5 px-2">{r.letterhead_name || "-"}</td>
                  <td className="py-1.5 px-2">{r.mobile}</td>
                  <td className="py-1.5 px-2">{r.darshan_type_name}</td>
                  <td className="py-1.5 px-2">{r.booking_type === "ADVANCED" ? "Advanced" : "Current"}</td>
                  <td className="py-1.5 px-2 text-right">{r.num_devotees} ({r.paid_devotees ?? "-"}/{r.free_devotees ?? "-"})</td>
                  <td className="py-1.5 px-2 text-right">₹{(r.amount || 0).toLocaleString("en-IN")}</td>
                  <td className="py-1.5 px-2">{handledBy(r, "counter_name")}</td>
                  <td className="py-1.5 px-2">{handledBy(r, "shift_name")}</td>
                  <td className="py-1.5 px-2 whitespace-nowrap">{r.status.replace(/_/g, " ")}</td>
                  <td className="py-1.5 px-2 whitespace-nowrap" data-testid={`details-verified-at-${i}`}>{r.verified_at ? fmtDateTime(r.verified_at) : "-"}</td>
                  <td className="py-1.5 px-2" data-testid={`details-verified-by-${i}`}>{r.verified_by || "-"}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-maroon-800 bg-gold-50 font-bold" data-testid="details-totals">
                <td className="py-2 px-2" colSpan={11}>Total: {data.totals.count} records{data.totals.cancelled ? ` (${data.totals.cancelled} cancelled/rejected not counted)` : ""}</td>
                <td className="py-2 px-2 text-right">{data.totals.devotees} ({data.totals.paid_devotees}/{data.totals.free_devotees})</td>
                <td className="py-2 px-2 text-right">₹{data.totals.amount.toLocaleString("en-IN")}</td>
                <td colSpan={5} />
              </tr>
            </tbody>
          </table>
          <div className="flex justify-between mt-10 text-xs text-slate-600">
            <div className="text-center"><p className="border-t border-slate-400 pt-1 px-6">Counter Signature</p></div>
            <div className="text-center"><p className="border-t border-slate-400 pt-1 px-6">Superintendent</p></div>
            <div className="text-center"><p className="border-t border-slate-400 pt-1 px-6">Executive Officer</p></div>
          </div>
        </div>
      )}
    </div>
  );
}

// Entrance verification (darshan completed) by verification date, gate and shift.
const VERIFY_HEAD = ["S.No", "Ticket No", "Transaction ID", "Devotee", "Darshan", "Devotees", "Verified At", "Verified By", "Gate / Counter", "Shift"];

function VerificationReport() {
  const { user } = useAuth();
  const [meta, setMeta] = useState({ counters: [], shifts: [], temples: [] });
  const [f, setF] = useState({ frm: "", to: "", shift_id: "", counter_id: "", temple_id: "" });
  const [data, setData] = useState(null);
  const reportRef = useRef(null);
  useEffect(() => { api.get("/meta/form-data").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  const run = () => {
    const q = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && q.set(k, v));
    api.get(`/reports/verification?${q}`).then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e)));
  };
  useEffect(() => { run(); }, []); // eslint-disable-line
  const temple = meta.temples.find((t) => t.temple_id === (f.temple_id || user.temple_id));
  const gate = meta.counters.find((c) => c.id === f.counter_id);
  const shift = meta.shifts.find((s) => s.id === f.shift_id);
  const title = "Entrance Verification Report" + (gate ? ` · Gate: ${gate.name}` : "") + (shift ? ` · Shift: ${shift.name}` : "");
  const row = (r, i) => [i + 1, r.ticket?.ticket_no || "-", r.transaction_id, r.devotee_name, r.darshan_type_name, r.num_devotees,
    fmtDateTime(r.verified_at), r.verified_by || "-", r.verification?.counter_name || "-", r.verification?.shift_name || "-"];
  const downloadPdf = () => {
    if (!data || data.rows.length === 0) { toast.error("No data to export"); return; }
    makePdf({
      temple, landscape: true, fontSize: 7.5, title: title.replace(/·/g, "-"), head: VERIFY_HEAD, body: data.rows.map(row),
      foot: ["", "", "", "", "Total", data.totals.devotees, `${data.totals.count} verified`, "", "", ""],
      filename: `Verification_${todayFile()}.pdf`,
    });
  };
  const sel = (key, label, items, valueKey, labelKey) => (
    <div><Label className="text-xs">{label}</Label>
      <Select value={f[key]} onValueChange={(v) => setF({ ...f, [key]: v === "ALL" ? "" : v })}>
        <SelectTrigger data-testid={`verify-filter-${key}`}><SelectValue placeholder="All" /></SelectTrigger>
        <SelectContent><SelectItem value="ALL">All</SelectItem>{items.map((i) => <SelectItem key={i[valueKey]} value={i[valueKey]}>{i[labelKey]}</SelectItem>)}</SelectContent>
      </Select></div>
  );
  return (
    <div>
      <Card className="shadow-sm mb-4">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <div><Label className="text-xs">From Date</Label><Input type="date" value={f.frm} onChange={(e) => setF({ ...f, frm: e.target.value })} data-testid="verify-from" /></div>
          <div><Label className="text-xs">To Date</Label><Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="verify-to" /></div>
          {user.role === "SUPER_ADMIN" && sel("temple_id", "Temple", meta.temples, "temple_id", "name")}
          {sel("shift_id", "Shift", meta.shifts, "id", "name")}
          {sel("counter_id", "Gate / Counter", meta.counters, "id", "name")}
          <div className="flex gap-2">
            <Button onClick={run} data-testid="verify-run-btn" className="bg-maroon-700 hover:bg-maroon-800 text-white">View</Button>
            <Button onClick={downloadPdf} variant="outline" title="Download PDF (A4 landscape)" data-testid="verify-pdf-btn"><FileDown className="w-4 h-4" /></Button>
            <Button onClick={() => reportRef.current && printHtml(reportRef.current.outerHTML, "a4-page", `Verification_Report_${new Date().toLocaleDateString("en-CA")}`)} variant="outline" title="Print" data-testid="verify-print-btn"><Printer className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>
      {data && (
        <div ref={reportRef} className="print-area print-a4 bg-white border border-[#E5D9C5] rounded-md shadow-sm p-6 overflow-x-auto" data-testid="verification-report">
          <ReportHeader title={title} temple={temple} />
          <table className="w-full text-xs border border-slate-300">
            <thead><tr className="bg-maroon-800 text-white uppercase tracking-wider">
              {VERIFY_HEAD.map((h) => <th key={h} className={`py-2 px-2 whitespace-nowrap ${h === "Devotees" ? "text-right" : "text-left"}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={10} className="py-6 text-center text-slate-400">No verified tickets for the selected filters</td></tr>}
              {data.rows.map((r, i) => (
                <tr key={r.transaction_id} className="border-t border-slate-200" data-testid={`verify-row-${i}`}>
                  {row(r, i).map((v, j) => <td key={j} className={`py-1.5 px-2 ${j === 5 ? "text-right" : ""} ${j === 1 || j === 2 ? "font-mono" : ""} ${j === 3 ? "font-semibold" : ""}`}>{v}</td>)}
                </tr>
              ))}
              <tr className="border-t-2 border-maroon-800 bg-gold-50 font-bold" data-testid="verify-totals">
                <td className="py-2 px-2" colSpan={5}>Total verified: {data.totals.count}</td>
                <td className="py-2 px-2 text-right">{data.totals.devotees}</td>
                <td colSpan={4} />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const admin = ["SUPER_ADMIN", "TEMPLE_ADMIN"].includes(user.role);
  const canSeeApprovals = ["SUPER_ADMIN", "TEMPLE_ADMIN", "SUPERVISOR"].includes(user.role);
  return (
    <div data-testid="reports-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900 mb-5">Protocol Reports</h1>
      <Tabs defaultValue="dcr">
        <TabsList className="bg-white border border-[#E5D9C5] flex-wrap h-auto">
          <TabsTrigger value="dcr" data-testid="tab-dcr">DCR Report</TabsTrigger>
          <TabsTrigger value="temple" data-testid="tab-temple">Abstract</TabsTrigger>
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="verification" data-testid="tab-verification">Verification</TabsTrigger>
          <TabsTrigger value="daily" data-testid="tab-daily">Day-wise</TabsTrigger>
          {canSeeApprovals && <TabsTrigger value="approvals" data-testid="tab-approvals">Approval Report</TabsTrigger>}
          {admin && <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">WhatsApp Log</TabsTrigger>}
          {admin && <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>}
        </TabsList>
        <TabsContent value="dcr" className="mt-4"><DCR /></TabsContent>
        <TabsContent value="temple" className="mt-4"><TempleAbstract /></TabsContent>
        <TabsContent value="details" className="mt-4"><DetailsReport /></TabsContent>
        <TabsContent value="verification" className="mt-4"><VerificationReport /></TabsContent>
        <TabsContent value="daily" className="mt-4"><DailyReport /></TabsContent>
        {canSeeApprovals && <TabsContent value="approvals" className="mt-4"><ApprovalReport /></TabsContent>}
        {admin && <TabsContent value="whatsapp" className="mt-4"><WhatsAppLog /></TabsContent>}
        {admin && <TabsContent value="audit" className="mt-4"><AuditLog /></TabsContent>}
      </Tabs>
    </div>
  );
}
